import {
  Box,
  Button,
  ColorPicker as ChakraColorPicker,
  ColorPickerChannelSlider,
  HStack,
  NativeSelect,
  Popover,
  Portal,
  Stack,
  Text,
  VStack,
  chakra,
  parseColor
} from "@chakra-ui/react"
import useMostRecent from "@pathwright/utils/src/storage/useMostRecent"
import { useEffect, useMemo, useRef, useState } from "react"
import { AugmentationPopover } from "../augmentation/AugmentationDialog"

type Rgba = {
  r: number
  g: number
  b: number
  a: number
}

type ColorSwatch = {
  label: string
  value: Pick<Rgba, "r" | "g" | "b">
  alpha?: number
  isCurrent?: boolean
}

type AdvancedColorFormat = "rgba" | "hexa" | "hsla"
type OutputFormat = "rgba" | "hex"

const ADVANCED_COLOR_FORMATS: AdvancedColorFormat[] = ["rgba", "hexa", "hsla"]

const DEFAULT_RECENT_KEY = "recent-color-picker-swatches"

const DEFAULT_COLOR: Rgba = {
  r: 0,
  g: 0,
  b: 0,
  a: 0.3
}

const COLOR_SWATCHES: ColorSwatch[] = [
  { label: "Black", value: { r: 0, g: 0, b: 0 } },
  { label: "White", value: { r: 255, g: 255, b: 255 } },
  { label: "Blue", value: { r: 13, g: 113, b: 251 } },
  { label: "Red", value: { r: 255, g: 59, b: 48 } },
  { label: "Green", value: { r: 52, g: 199, b: 89 } },
  { label: "Yellow", value: { r: 255, g: 204, b: 0 } }
]

const DEFAULT_SWATCH_COLORS = COLOR_SWATCHES.map(
  (swatch) => `${swatch.value.r},${swatch.value.g},${swatch.value.b},1`
)

/** @deprecated Prefer `recent` + optional `recentKey`. Kept for tag/Quill migrations. */
export const RECENT_COLOR_PICKER_SWATCHES_KEY = DEFAULT_RECENT_KEY

function clamp(value: number, min: number, max: number) {
  if (Number.isNaN(value)) return min
  return Math.min(Math.max(value, min), max)
}

function toHexChannel(value: number) {
  return Math.round(value).toString(16).padStart(2, "0")
}

function rgbaFromChannels(
  r: number,
  g: number,
  b: number,
  a: number = 1
): Rgba {
  return {
    r: clamp(r, 0, 255),
    g: clamp(g, 0, 255),
    b: clamp(b, 0, 255),
    a: clamp(a, 0, 1)
  }
}

export function parseColorValue(value?: string | Rgba | null): Rgba {
  if (!value) return DEFAULT_COLOR

  if (typeof value === "object") {
    return rgbaFromChannels(
      Number(value.r),
      Number(value.g),
      Number(value.b),
      value.a == null ? 1 : Number(value.a)
    )
  }

  const normalizedValue = value.trim()
  if (!normalizedValue) return DEFAULT_COLOR

  const match = normalizedValue.match(/rgba?\(([^)]+)\)/i)
  if (match || /^\d+(\.\d+)?\s*,/.test(normalizedValue)) {
    const parts = (match ? match[1] : normalizedValue)
      .split(",")
      .map((part) => part.trim())
    const [r, g, b, a] = parts
    return rgbaFromChannels(
      Number(r),
      Number(g),
      Number(b),
      a == null || a === "" ? 1 : Number(a)
    )
  }

  try {
    const cssValue = normalizedValue.startsWith("#")
      ? normalizedValue
      : /^[0-9a-f]{3,8}$/i.test(normalizedValue)
      ? `#${normalizedValue}`
      : normalizedValue
    return parseColorValue(parseColor(cssValue).toString("rgba"))
  } catch {
    return DEFAULT_COLOR
  }
}

export function formatColor(rgba: Rgba, outputFormat: OutputFormat = "rgba") {
  if (outputFormat === "hex") {
    return `#${toHexChannel(rgba.r)}${toHexChannel(rgba.g)}${toHexChannel(
      rgba.b
    )}`
  }

  return `${Math.round(rgba.r)},${Math.round(rgba.g)},${Math.round(
    rgba.b
  )},${Number(rgba.a.toFixed(2))}`
}

function rgbaToCss(rgba: Rgba) {
  return `rgba(${Math.round(rgba.r)}, ${Math.round(rgba.g)}, ${Math.round(
    rgba.b
  )}, ${rgba.a})`
}

function colorStringToSwatch(color: string, index: number): ColorSwatch {
  const rgba = parseColorValue(color)
  return {
    label: `Swatch ${index + 1}`,
    value: { r: rgba.r, g: rgba.g, b: rgba.b },
    alpha: rgba.a
  }
}

function uniqColors(colors: string[]) {
  const seen = new Set<string>()
  return colors.filter((color) => {
    const key = formatColor(parseColorValue(color), "rgba")
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * Tracks most-recently-used color swatches in localStorage.
 * Shape: `{ list, index }` via shared `useMostRecent`.
 */
export function useRecentColorPickerSwatches(
  key: string = DEFAULT_RECENT_KEY,
  defaultSwatches: string[] = DEFAULT_SWATCH_COLORS
) {
  // Freeze the seed list so useMostRecent keeps a stable initial value.
  const seed = useMemo(() => defaultSwatches, [])

  return useMostRecent<string>(key, seed, { maintainOrder: true })
}

function isSameColor(a: Rgba, b: Pick<Rgba, "r" | "g" | "b">) {
  return (
    Math.round(a.r) === Math.round(b.r) &&
    Math.round(a.g) === Math.round(b.g) &&
    Math.round(a.b) === Math.round(b.b)
  )
}

function isSelectedSwatch(color: Rgba, swatch: ColorSwatch) {
  if (!isSameColor(color, swatch.value)) return false
  return (
    typeof swatch.alpha === "undefined" ||
    Math.abs(color.a - swatch.alpha) < 0.005
  )
}

function AdvancedColorPicker({
  color,
  onChange,
  enableAlpha = true
}: {
  color: Rgba
  onChange: (color: Rgba) => void
  enableAlpha?: boolean
}) {
  const [format, setFormat] = useState<AdvancedColorFormat>("rgba")
  const parsedColor = useMemo(() => parseColor(rgbaToCss(color)), [color])
  const currentHexa = useMemo(() => parsedColor.toString("hexa"), [parsedColor])
  const [hexaValue, setHexaValue] = useState(currentHexa)

  useEffect(() => {
    setHexaValue(currentHexa)
  }, [currentHexa])

  function handleHexaChange(value: string) {
    const nextValue = value
      .replace(/[^#0-9a-f]/gi, "")
      .replace(/(?!^)#/g, "")
      .slice(0, value.startsWith("#") ? 9 : 8)

    setHexaValue(nextValue)

    if (!/^#?[0-9a-f]{6}([0-9a-f]{2})?$/i.test(nextValue)) return

    try {
      onChange(parseColorValue(parseColor(nextValue).toString("rgba")))
    } catch {
      // Ignore invalid intermediate text entry.
    }
  }

  function handleCycleFormat() {
    const currentIndex = ADVANCED_COLOR_FORMATS.indexOf(format)
    const nextIndex = (currentIndex + 1) % ADVANCED_COLOR_FORMATS.length
    setFormat(ADVANCED_COLOR_FORMATS[nextIndex])
  }

  return (
    <ChakraColorPicker.Root
      open
      value={parsedColor}
      format={format === "hexa" ? "rgba" : format}
      defaultFormat="rgba"
      onValueChange={(details) => {
        onChange(parseColorValue(details.value.toString("rgba")))
      }}
    >
      <ChakraColorPicker.Content
        position="static"
        w="300px"
        p={0}
        borderWidth={0}
        boxShadow="none"
      >
        <VStack align="stretch" gap={3}>
          <ChakraColorPicker.Area h="180px" />
          <Stack>
            <ColorPickerChannelSlider channel="hue" />
            {enableAlpha && <ColorPickerChannelSlider channel="alpha" />}
          </Stack>
          <HStack gap={4} justify="flex-start">
            <ChakraColorPicker.EyeDropper size="sm" variant="outline" />
            <Button
              size="sm"
              variant="outline"
              minW="32px"
              justifyContent="center"
              onClick={handleCycleFormat}
            >
              F
            </Button>
            <NativeSelect.Root size="sm" flex="1">
              <NativeSelect.Field
                value={format}
                onChange={(event) =>
                  setFormat(event.target.value as AdvancedColorFormat)
                }
              >
                <option value="rgba">RGBA</option>
                <option value="hexa">HEXA</option>
                <option value="hsla">HLSA</option>
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </HStack>
          {format === "hexa" ? (
            <chakra.input
              value={hexaValue}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleHexaChange(event.target.value)
              }
              aria-label="Hex alpha color"
              maxLength={9}
              fontSize="sm"
              h="9"
              w="13ch"
              px={3}
              borderWidth="1px"
              borderRadius="md"
              borderColor="border"
              outline="none"
            />
          ) : (
            <HStack gap={2}>
              {format === "rgba" ? (
                <>
                  <ChakraColorPicker.ChannelInput
                    channel="red"
                    flex="1"
                    minW={0}
                  />
                  <ChakraColorPicker.ChannelInput
                    channel="green"
                    flex="1"
                    minW={0}
                  />
                  <ChakraColorPicker.ChannelInput
                    channel="blue"
                    flex="1"
                    minW={0}
                  />
                  {enableAlpha && (
                    <ChakraColorPicker.ChannelInput
                      channel="alpha"
                      flex="1"
                      minW={0}
                    />
                  )}
                </>
              ) : (
                <>
                  <ChakraColorPicker.ChannelInput
                    channel="hue"
                    flex="1"
                    minW={0}
                  />
                  <ChakraColorPicker.ChannelInput
                    channel="saturation"
                    flex="1"
                    minW={0}
                  />
                  <ChakraColorPicker.ChannelInput
                    channel="lightness"
                    flex="1"
                    minW={0}
                  />
                  {enableAlpha && (
                    <ChakraColorPicker.ChannelInput
                      channel="alpha"
                      flex="1"
                      minW={0}
                    />
                  )}
                </>
              )}
            </HStack>
          )}
        </VStack>
      </ChakraColorPicker.Content>
    </ChakraColorPicker.Root>
  )
}

function ColorPicker({
  disabled,
  color: colorProp,
  initialColor: initialColorProp,
  onChange,
  outputFormat = "rgba",
  disableAlpha = false,
  swatches: swatchesProp,
  recent = false,
  recentKey,
  onOpenChange,
  autoFocus = true,
  children
}: {
  disabled?: boolean
  color: string | Rgba
  initialColor?: string | Rgba
  onChange: (color: string) => void
  outputFormat?: OutputFormat
  /**
   * When true, hide alpha controls and force alpha to 1 on commit.
   * Also forced when `outputFormat` is `"hex"`.
   */
  disableAlpha?: boolean
  /** Overrides the built-in swatch palette. */
  swatches?: string[]
  /** When true, track/read most-recent colors from localStorage. */
  recent?: boolean
  /** localStorage key used when `recent` is true. */
  recentKey?: string
  onOpenChange?: (details: { open: boolean }) => void
  autoFocus?: boolean
  children?: React.ReactNode
}) {
  const enableAlpha = outputFormat !== "hex" && !disableAlpha
  const resolvedRecentKey = recent ? recentKey || DEFAULT_RECENT_KEY : undefined

  const color = parseColorValue(colorProp)
  const initialColor = parseColorValue(initialColorProp ?? colorProp)
  const colorCss = rgbaToCss(color)

  const seedSwatches = swatchesProp ?? DEFAULT_SWATCH_COLORS
  const [recentSwatches, updateRecent] = useRecentColorPickerSwatches(
    // Empty key skips persistence when recent tracking is off.
    recent ? resolvedRecentKey! : "",
    seedSwatches
  )

  // Stash the last committed color and only persist it when the popover closes.
  const pendingRecentRef = useRef<string | null>(null)

  const paletteColors = useMemo(() => {
    if (recent) {
      // Prefer recent colors, then fall back to provided/default swatches
      // (e.g. Quill toolbar options) so custom colors stay visible.
      return uniqColors([
        ...recentSwatches.list,
        ...(swatchesProp ?? [])
      ]).slice(0, Math.max(recentSwatches.list.length, seedSwatches.length, 12))
    }
    return swatchesProp ?? DEFAULT_SWATCH_COLORS
  }, [recent, swatchesProp, recentSwatches.list, seedSwatches.length])

  const swatches: ColorSwatch[] = [
    {
      label: "Current",
      value: {
        r: initialColor.r,
        g: initialColor.g,
        b: initialColor.b
      },
      alpha: initialColor.a,
      isCurrent: true
    },
    ...paletteColors.map(colorStringToSwatch)
  ].slice(0, 7) // cap at 7 swatches to keep to single line

  function commitColor(nextColor: Rgba) {
    const colorToCommit = enableAlpha ? nextColor : { ...nextColor, a: 1 }
    const formatted = formatColor(colorToCommit, outputFormat)
    if (recent) pendingRecentRef.current = formatted
    onChange(formatted)
  }

  function handleOpenChange(details: { open: boolean }) {
    if (!details.open && recent && pendingRecentRef.current) {
      updateRecent(pendingRecentRef.current)
      pendingRecentRef.current = null
    }
    onOpenChange?.(details)
  }

  return (
    <AugmentationPopover
      positioning={{ placement: "bottom-end" }}
      lazyMount
      onOpenChange={handleOpenChange}
      autoFocus={autoFocus}
      onFocusOutside={(event) => {
        if (!autoFocus) {
          event.preventDefault()
        }
      }}
    >
      <Popover.Trigger asChild>
        {children || (
          <Button
            aria-label="Choose color"
            disabled={disabled}
            minW="42px"
            h="42px"
            p={1}
            borderRadius="md"
            borderWidth="1px"
            borderColor="border"
            bg="bg"
          >
            <Box
              boxSize="full"
              borderRadius="sm"
              bg={colorCss}
              boxShadow="inset 0 0 0 1px rgba(0, 0, 0, 0.18)"
            />
          </Button>
        )}
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content w="290px" zIndex="popover" borderRadius="card">
            <Popover.Arrow />
            <Popover.Body p={4}>
              <VStack align="stretch" gap={4}>
                {enableAlpha && (
                  <VStack align="stretch" gap={2}>
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="semibold" m={0}>
                        Opacity
                      </Text>
                      <Text fontSize="xs" color="fg.muted" m={0}>
                        {Math.round(color.a * 100)}%
                      </Text>
                    </HStack>
                    <chakra.input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={Math.round(color.a * 100)}
                      accentColor="gray.700"
                      css={{
                        "&": {
                          appearance: "none",
                          width: "100%",
                          height: "16px",
                          background: "transparent"
                        },
                        "&::-webkit-slider-runnable-track": {
                          height: "4px",
                          borderRadius: "999px",
                          background: "var(--chakra-colors-gray-200)"
                        },
                        "&::-webkit-slider-thumb": {
                          appearance: "none",
                          width: "16px",
                          height: "16px",
                          borderRadius: "999px",
                          background: "var(--chakra-colors-gray-700)",
                          marginTop: "-6px"
                        },
                        "&::-moz-range-track": {
                          height: "4px",
                          borderRadius: "999px",
                          background: "var(--chakra-colors-gray-200)"
                        },
                        "&::-moz-range-thumb": {
                          width: "16px",
                          height: "16px",
                          border: 0,
                          borderRadius: "999px",
                          background: "var(--chakra-colors-gray-700)"
                        }
                      }}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        commitColor({
                          ...color,
                          a: Number(event.target.value) / 100
                        })
                      }}
                    />
                  </VStack>
                )}
                <HStack gap={2} justify="space-between">
                  {swatches.map((swatch) => {
                    const swatchColor = {
                      ...swatch.value,
                      a: enableAlpha ? swatch.alpha ?? color.a : 1
                    }

                    return (
                      <Button
                        key={swatch.label}
                        aria-label={swatch.label}
                        title={swatch.label}
                        minW="28px"
                        boxSize="28px"
                        p={0}
                        borderRadius="full"
                        borderWidth="2px"
                        borderColor={
                          isSelectedSwatch(color, swatch)
                            ? "brand.500"
                            : swatch.isCurrent
                            ? "fg.muted"
                            : "border"
                        }
                        bg={rgbaToCss(swatchColor)}
                        boxShadow="inset 0 0 0 1px rgba(0, 0, 0, 0.16)"
                        onClick={() => commitColor(swatchColor)}
                      />
                    )
                  })}
                </HStack>
                <AugmentationPopover
                  positioning={{ placement: "right-start" }}
                  autoFocus={autoFocus}
                  onFocusOutside={(event) => {
                    if (!autoFocus) {
                      event.preventDefault()
                    }
                  }}
                >
                  <Popover.Trigger asChild>
                    <Button size="sm" variant="outline" w="full">
                      Advanced
                    </Button>
                  </Popover.Trigger>
                  <Portal>
                    <Popover.Positioner>
                      <Popover.Content
                        w="auto"
                        zIndex="popover"
                        borderRadius="card"
                      >
                        <Popover.Arrow />
                        <Popover.Body p={4}>
                          <AdvancedColorPicker
                            color={color}
                            onChange={commitColor}
                            enableAlpha={enableAlpha}
                          />
                        </Popover.Body>
                      </Popover.Content>
                    </Popover.Positioner>
                  </Portal>
                </AugmentationPopover>
              </VStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </AugmentationPopover>
  )
}

export default ColorPicker
